import { useCallback } from 'react';
import { runAiTurn } from '../../services/ai/engine';

export function useAiStreamTurn({ abortRef, streamFinishRef, setContextTokens }) {
  // One model turn via the BYO-key engine (services/ai/engine.js) — the
  // same callback protocol the retired Phoenix channel spoke, now backed by
  // a single non-streamed provider call (the whole text arrives as one
  // text_delta; see providers/providerHttp.js for why streaming is off).
  return useCallback((anthropicMessages, onTextDelta) => {
    return new Promise((resolve, reject) => {
      let textContent = '';
      const toolUseBlocks = [];
      let settled = false;
      let abortEngine = null;

      const finish = (stopReason) => {
        if (settled) return;
        settled = true;
        streamFinishRef.current = null;
        abortEngine?.();
        resolve({ textContent, toolUseBlocks, stopReason: stopReason || 'end_turn' });
      };
      // COMMS-15: carry whatever arrived before the failure on the rejection
      // itself, so the catch block in sendMessage can fold it into
      // anthropicHistoryRef instead of silently stranding it on the turn
      // before this one (mirrors the abort branch below, which keeps partial
      // text the same way via its own local variables).
      const settleReject = (err) => {
        if (settled) return;
        settled = true;
        streamFinishRef.current = null;
        abortEngine?.();
        err.partialTextContent = textContent;
        err.partialToolUseBlocks = toolUseBlocks;
        reject(err);
      };
      // Lets the Stop button force this turn to resolve immediately instead
      // of waiting for the provider call (the loop then sees abortRef +
      // breaks); finish() aborts the engine so its late result is discarded.
      streamFinishRef.current = () => finish('aborted');

      abortEngine = runAiTurn(anthropicMessages, {
        onTextDelta: (text) => {
          if (abortRef.current) return;
          textContent += text;
          onTextDelta(text);
        },
        onToolUse: ({ id, name, input }) => {
          if (abortRef.current) return;
          toolUseBlocks.push({ id, name, input });
        },
        onUsage: (usage) => {
          if (abortRef.current) return;
          // The input side (raw + cached) = the size of the prompt for this
          // turn, i.e. the current conversation context.
          const input = usage?.input_tokens || 0;
          const cacheRead = usage?.cache_read_input_tokens || 0;
          const cacheCreate = usage?.cache_creation_input_tokens || 0;
          const promptTokens = input + cacheRead + cacheCreate;
          if (promptTokens > 0) setContextTokens(promptTokens);
        },
        onEndTurn: ({ stop_reason }) => finish(stop_reason),
        onError: (msg) => settleReject(new Error(msg || 'AI request failed')),
      });
    });
  }, [abortRef, streamFinishRef, setContextTokens]);
}
